import "@/App.css";
import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = {
  bg: "#10223D",
  bg2: "#0C1B31",
  cream: "#F5EFDF",
  label: "#B7C0CF",
  muted: "#7E8A9E",
  accent: "#C8BFA6",
  gold: "#C9A460",
};

const FONT_STACK_SANS =
  "'IBM Plex Sans', -apple-system, 'Helvetica Neue', Arial, sans-serif";
const FONT_STACK_MONO = "'JetBrains Mono', ui-monospace, monospace";
const FONT_STACK_SERIF = "'Playfair Display', Georgia, serif";

const STATE_REPORT_URL =
  process.env.REACT_APP_STATE_REPORT_URL ||
  "https://strategic-report-hub.preview.emergentagent.com/";

/* States with finalized, deliverable briefings */
const READY_STATES = new Set([
  "Alabama",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "Florida",
  "Georgia",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
]);

const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut",
  "Delaware","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa",
  "Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan",
  "Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada",
  "New Hampshire","New Jersey","New Mexico","New York","North Carolina",
  "North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island",
  "South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont",
  "Virginia","Washington","West Virginia","Wisconsin","Wyoming",
];

function GoogleFonts() {
  return (
    <link
      href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,500;0,600;0,700;1,500;1,600&family=IBM+Plex+Sans:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap"
      rel="stylesheet"
    />
  );
}

/* --------------------------------------------------------------
   LANDING PAGE  –  /
   -------------------------------------------------------------- */
function Landing() {
  const navigate = useNavigate();
  const [videoOpen, setVideoOpen] = React.useState(false);

  return (
    <div
      data-testid="landing-page"
      style={{
        minHeight: "100vh",
        background: COLORS.bg,
        color: COLORS.label,
        fontFamily: FONT_STACK_SANS,
        padding: "3rem 2rem 4rem",
      }}
    >
      <GoogleFonts />

      {/* Responsive rules for mobile: lift the floating logo onto its own row
          above the eyebrow so it never overlaps the title text. */}
      <style>{`
        @media (max-width: 720px) {
          .mendrx-floating-logo img {
            height: 36px !important;
          }
          .vp-quote {
            font-size: 22px !important;
            line-height: 1.22 !important;
          }
          .vp-attr {
            font-size: 11px !important;
            margin-top: 14px !important;
          }
          .vp-response {
            font-size: 16px !important;
            margin-top: 20px !important;
            padding-left: 14px !important;
          }
        }
        @media (max-width: 960px) {
          .hero-grid {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
          }
        }
      `}</style>

      <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative" }}>
        {/* Top header row — MendRx logo sits on its own clean row */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            marginBottom: 32,
          }}
        >
          <a
            href="https://www.mendrx.ai"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mendrx-logo-link"
            className="mendrx-floating-logo"
            style={{ display: "block", lineHeight: 0 }}
          >
            <img
              src="/mendrx-logo.png"
              alt="MendRx"
              style={{
                height: 48,
                width: "auto",
                display: "block",
              }}
              data-testid="mendrx-logo-img"
            />
          </a>
        </div>

        {/* ───────────────────────────────────────────────────────────
            VICE PRESIDENT'S STATEMENT — Statement of Record
            Full-width, top-of-hero. Prosecutorial, not promotional.
            ─────────────────────────────────────────────────────────── */}
        <section
          data-testid="vp-statement-block"
          aria-label="Vice President statement"
          style={{
            borderTop: `1px solid rgba(200,191,166,0.18)`,
            borderBottom: `1px solid rgba(200,191,166,0.18)`,
            padding: "44px 0 40px",
            marginBottom: 56,
          }}
        >
          <div
            style={{
              fontFamily: FONT_STACK_MONO,
              fontSize: 10,
              letterSpacing: "0.34em",
              textTransform: "uppercase",
              color: COLORS.gold,
              marginBottom: 22,
            }}
            data-testid="vp-statement-kicker"
          >
            § Statement of Record
          </div>

          <blockquote
            data-testid="vp-statement-quote"
            className="vp-quote"
            style={{
              margin: 0,
              fontFamily: FONT_STACK_SERIF,
              fontWeight: 500,
              fontSize: "clamp(26px, 3.4vw, 44px)",
              lineHeight: 1.18,
              letterSpacing: "-0.012em",
              color: COLORS.cream,
              maxWidth: 980,
            }}
          >
            "We encourage people to work with us. We want to help you use
            technology and other tools to get rid of the fraud, to get to the
            root of the fraud."
          </blockquote>

          <div
            data-testid="vp-statement-attribution"
            className="vp-attr"
            style={{
              marginTop: 18,
              fontFamily: FONT_STACK_MONO,
              fontSize: 12,
              lineHeight: 1.55,
              letterSpacing: "0.08em",
              color: COLORS.muted,
            }}
          >
            — Vice President JD Vance
            <br />
            White House, May 13, 2026
          </div>

          <div
            data-testid="mendrx-response"
            className="vp-response"
            style={{
              marginTop: 28,
              paddingLeft: 18,
              borderLeft: `3px solid ${COLORS.gold}`,
              fontFamily: FONT_STACK_SERIF,
              fontWeight: 700,
              fontSize: "clamp(18px, 1.9vw, 24px)",
              lineHeight: 1.35,
              letterSpacing: "-0.005em",
              color: COLORS.gold,
              maxWidth: 920,
            }}
          >
            MendRx prevents Medicare and Medicaid fraud before the claim is
            paid — by serializing reimbursement at the point of dispense, where
            the fraud actually originates.
          </div>
        </section>

        {/* Hero: 2-column on desktop (text + video), single column on mobile */}
        <div
          className="hero-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr)",
            gap: 56,
            alignItems: "start",
            marginBottom: 8,
          }}
        >
          <div>
            {/* Eyebrow */}
            <div
              style={{
                fontFamily: FONT_STACK_MONO,
                fontSize: 11,
                letterSpacing: "0.32em",
                color: COLORS.gold,
                textTransform: "uppercase",
                marginBottom: 18,
              }}
              data-testid="eyebrow-label"
            >
              § Governor's Briefing
            </div>

            {/* Title */}
            <h1
              style={{
                fontFamily: FONT_STACK_SERIF,
                fontWeight: 500,
                fontSize: "clamp(40px, 5.5vw, 64px)",
                lineHeight: 1.0,
                letterSpacing: "-0.02em",
                color: COLORS.cream,
                marginBottom: 24,
              }}
              data-testid="page-title"
            >
              30 Days to{" "}
              <span
                style={{
                  fontStyle: "italic",
                  color: COLORS.gold,
                  fontWeight: 500,
                }}
              >
                Strengthen
              </span>
              <br />
              Medicaid
            </h1>

            {/* Lede */}
            <p
              style={{
                fontSize: 17,
                lineHeight: 1.55,
                color: "#FFFFFF",
                marginBottom: 0,
                fontWeight: 400,
              }}
              data-testid="page-lede"
            >
              Three briefings, one framework. Start with the one-page executive summary on the CMS 30-day directive, go deeper with the full Governors' Briefing deck, or open your state's fully audited verification report. Each is MendRx-built and routes directly to the Executive Briefing Desk.
            </p>
          </div>

          {/* Video promo — right column */}
          <VideoPromoPanel onPlay={() => setVideoOpen(true)} />
        </div>

        {/* Spacer between hero and cards */}
        <div style={{ height: 40 }} />

        {/* Two cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
            gap: 24,
            marginBottom: 24,
          }}
        >
          <DocumentCard
            kicker="§  One-Pager"
            title="30 Days to Strengthen Medicaid"
            meta="1 page · Letter · Governor's Briefing"
            description="The executive one-page brief. CMS 30-day directive framing, key stats, and contact for the Executive Briefing Desk."
            onView={() => navigate("/view/onepager")}
            downloadHref={`${API}/briefing/download`}
            testIdPrefix="onepager"
          />

          <DocumentCard
            kicker="§  Full Deck"
            title="MENDRx Governors' Briefing — Full Deck"
            meta="10 pages · Letter · Governor's Briefing"
            description="The complete briefing deck covering directive context, verification framework, pilot model, and investment outline."
            onView={() => navigate("/view/deck")}
            downloadHref={`${API}/deck/download`}
            testIdPrefix="deck"
          />
        </div>

        {/* Full-width State Reports box */}
        <StateReportsBox />

        <div
          style={{
            paddingTop: 20,
            borderTop: `1px solid rgba(200,191,166,0.2)`,
            fontFamily: FONT_STACK_MONO,
            fontSize: 10,
            letterSpacing: "0.28em",
            textTransform: "uppercase",
            color: COLORS.muted,
          }}
          data-testid="page-footer"
        >
          Confidential · MENDRx · mendrx.ai
        </div>
      </div>

      {videoOpen && <VideoModal onClose={() => setVideoOpen(false)} />}
    </div>
  );
}

/* --------------------------------------------------------------
   VIDEO PROMO PANEL  –  right-column hero companion
   -------------------------------------------------------------- */
function VideoPromoPanel({ onPlay }) {
  return (
    <button
      type="button"
      onClick={onPlay}
      data-testid="video-promo-panel"
      aria-label="Play MendRx briefing video"
      style={{
        position: "relative",
        width: "100%",
        aspectRatio: "16 / 9",
        background: "#0A1628",
        border: `1px solid rgba(201,164,96,0.35)`,
        padding: 0,
        cursor: "pointer",
        overflow: "hidden",
        display: "block",
        fontFamily: "inherit",
        textAlign: "left",
        transition: "border-color 200ms ease, transform 200ms ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = COLORS.gold;
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "rgba(201,164,96,0.35)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      <img
        src={`${API}/video/poster`}
        alt=""
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
          opacity: 0.78,
        }}
      />
      {/* Overlay tint for legibility */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(10,22,40,0.05) 0%, rgba(10,22,40,0.55) 100%)",
        }}
      />
      {/* Play button */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            width: 76,
            height: 76,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.92)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 10px 32px rgba(0,0,0,0.45)",
          }}
        >
          {/* Play triangle */}
          <div
            style={{
              width: 0,
              height: 0,
              marginLeft: 5,
              borderTop: "13px solid transparent",
              borderBottom: "13px solid transparent",
              borderLeft: `20px solid ${COLORS.bg}`,
            }}
          />
        </div>
      </div>
      {/* Caption strip */}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          padding: "14px 18px",
          background:
            "linear-gradient(0deg, rgba(8,16,28,0.92) 0%, rgba(8,16,28,0) 100%)",
          color: COLORS.cream,
        }}
      >
        <div
          style={{
            fontFamily: FONT_STACK_MONO,
            fontSize: 10,
            letterSpacing: "0.32em",
            textTransform: "uppercase",
            color: COLORS.gold,
            marginBottom: 6,
          }}
        >
          § Watch
        </div>
        <div
          style={{
            fontFamily: FONT_STACK_SERIF,
            fontSize: 18,
            fontWeight: 500,
            lineHeight: 1.2,
            letterSpacing: "-0.005em",
          }}
        >
          Inside MendRx — the verification framework
        </div>
      </div>
    </button>
  );
}

/* --------------------------------------------------------------
   VIDEO MODAL  –  fullscreen lightbox player
   -------------------------------------------------------------- */
function VideoModal({ onClose }) {
  // Close on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      data-testid="video-modal"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(4,10,20,0.92)",
        backdropFilter: "blur(6px)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "32px",
      }}
    >
      <button
        type="button"
        onClick={onClose}
        data-testid="video-modal-close"
        aria-label="Close video"
        style={{
          position: "absolute",
          top: 24,
          right: 24,
          background: "transparent",
          color: COLORS.cream,
          border: `1px solid rgba(245,239,223,0.4)`,
          fontFamily: FONT_STACK_MONO,
          fontSize: 11,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          padding: "10px 16px",
          cursor: "pointer",
          fontWeight: 600,
        }}
      >
        × Close
      </button>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(1280px, 100%)",
          aspectRatio: "16 / 9",
          background: "#000",
          boxShadow: "0 20px 80px rgba(0,0,0,0.6)",
          border: `1px solid rgba(201,164,96,0.28)`,
        }}
      >
        <video
          data-testid="video-modal-player"
          src={`${API}/video/promo`}
          poster={`${API}/video/poster`}
          controls
          autoPlay
          playsInline
          style={{
            width: "100%",
            height: "100%",
            display: "block",
            background: "#000",
          }}
        />
      </div>
    </div>
  );
}

/* --------------------------------------------------------------
   STATE REPORTS BOX  –  full-width, 50-state grid
   -------------------------------------------------------------- */
function StateReportsBox() {
  const [query, setQuery] = useState("");
  const [pendingState, setPendingState] = useState(null);

  const filtered = US_STATES.filter((s) =>
    s.toLowerCase().includes(query.trim().toLowerCase())
  );

  /* Ready states float to the top */
  const sorted = [...filtered].sort((a, b) => {
    const aReady = READY_STATES.has(a);
    const bReady = READY_STATES.has(b);
    if (aReady && !bReady) return -1;
    if (!aReady && bReady) return 1;
    return 0;
  });

  return (
    <div
      data-testid="state-reports-box"
      style={{
        background: COLORS.bg2,
        border: `1px solid rgba(200,191,166,0.22)`,
        padding: "32px 32px 28px",
        marginBottom: 48,
      }}
    >
      {/* Header row */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 8,
        }}
      >
        <div style={{ maxWidth: 680 }}>
          <div
            style={{
              fontFamily: FONT_STACK_MONO,
              fontSize: 10,
              letterSpacing: "0.32em",
              color: COLORS.gold,
              textTransform: "uppercase",
              marginBottom: 12,
            }}
          >
            §  State Policy Briefings
          </div>
          <h2
            style={{
              fontFamily: FONT_STACK_SERIF,
              fontWeight: 500,
              fontSize: 28,
              lineHeight: 1.15,
              color: COLORS.cream,
              marginBottom: 10,
              letterSpacing: "-0.01em",
            }}
          >
            Choose your state to view its tailored briefing.
          </h2>
          <p
            style={{
              fontSize: 14,
              lineHeight: 1.55,
              color: "#FFFFFF",
              marginBottom: 0,
            }}
          >
            State-specific reports present the MENDRx verification program with data
            and recommendations written for your jurisdiction. Each report is
            password-gated for the governor's office and CMS integrity team.
          </p>
        </div>

        {/* Search */}
        <div style={{ minWidth: 240 }}>
          <label
            htmlFor="state-search"
            style={{
              fontFamily: FONT_STACK_MONO,
              fontSize: 9,
              letterSpacing: "0.28em",
              color: COLORS.muted,
              textTransform: "uppercase",
              display: "block",
              marginBottom: 8,
            }}
          >
            Find State
          </label>
          <input
            id="state-search"
            data-testid="state-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="e.g. Florida"
            style={{
              width: "100%",
              background: COLORS.bg,
              border: `1px solid rgba(200,191,166,0.28)`,
              color: COLORS.cream,
              padding: "11px 14px",
              fontFamily: FONT_STACK_MONO,
              fontSize: 12,
              letterSpacing: "0.06em",
              outline: "none",
            }}
          />
        </div>
      </div>

      {/* Divider */}
      <div
        style={{
          height: 1,
          background: "rgba(200,191,166,0.2)",
          margin: "22px 0 24px",
        }}
      />

      {/* States grid */}
      <div
        data-testid="states-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
          gap: 10,
        }}
      >
        {sorted.map((state) => {
          const isReady = READY_STATES.has(state);
          const slug = state.toLowerCase().replace(/\s+/g, "-");
          const tileHref = isReady ? `/view/state/${slug}` : "#";
          return (
            <a
              key={state}
              href={tileHref}
              target={isReady ? "_blank" : undefined}
              rel={isReady ? "noopener noreferrer" : undefined}
              onClick={(e) => {
                e.preventDefault();
                if (isReady) {
                  window.open(tileHref, "_blank", "noopener,noreferrer");
                } else {
                  setPendingState(state);
                }
              }}
              data-testid={`state-tile-${slug}`}
              data-ready={isReady ? "true" : "false"}
              style={{
                background: isReady ? "#14304f" : COLORS.bg,
                border: isReady
                  ? `1px solid ${COLORS.gold}`
                  : `1px solid rgba(200,191,166,0.18)`,
                padding: "14px 16px",
                color: COLORS.cream,
                textDecoration: "none",
                fontFamily: FONT_STACK_SANS,
                fontSize: 14,
                fontWeight: 500,
                letterSpacing: "0.005em",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 8,
                transition: "border-color 160ms ease, background-color 160ms ease",
                position: "relative",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = COLORS.gold;
                e.currentTarget.style.background = "#13284a";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = isReady
                  ? COLORS.gold
                  : "rgba(200,191,166,0.18)";
                e.currentTarget.style.background = isReady ? "#14304f" : COLORS.bg;
              }}
            >
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                {isReady && (
                  <span
                    aria-label="Ready"
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: COLORS.gold,
                      boxShadow: `0 0 8px ${COLORS.gold}`,
                      display: "inline-block",
                    }}
                  />
                )}
                <span>{state}</span>
              </span>
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                {isReady && (
                  <span
                    style={{
                      fontFamily: FONT_STACK_MONO,
                      fontSize: 8.5,
                      letterSpacing: "0.22em",
                      textTransform: "uppercase",
                      color: COLORS.gold,
                    }}
                  >
                    Ready
                  </span>
                )}
                <span
                  style={{
                    fontFamily: FONT_STACK_MONO,
                    color: COLORS.gold,
                    fontSize: 13,
                    lineHeight: 1,
                  }}
                >
                  ›
                </span>
              </span>
            </a>
          );
        })}

        {sorted.length === 0 && (
          <div
            style={{
              gridColumn: "1 / -1",
              color: COLORS.muted,
              fontFamily: FONT_STACK_MONO,
              fontSize: 12,
              letterSpacing: "0.08em",
              padding: "24px 0",
              textAlign: "center",
            }}
          >
            No state matches "{query}".
          </div>
        )}
      </div>

      {/* Access note */}
      <div
        style={{
          marginTop: 22,
          paddingTop: 16,
          borderTop: "1px solid rgba(200,191,166,0.14)",
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
          fontFamily: FONT_STACK_MONO,
          fontSize: 10,
          letterSpacing: "0.24em",
          color: COLORS.muted,
          textTransform: "uppercase",
        }}
        data-testid="state-access-note"
      >
        <span>Password-Gated · Governor's Office Access</span>
        <span>50 Jurisdictions · Web + PDF</span>
      </div>

      {/* In-progress modal for non-ready states */}
      {pendingState && (
        <div
          data-testid="state-pending-modal"
          role="dialog"
          aria-modal="true"
          onClick={() => setPendingState(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(6, 14, 26, 0.78)",
            backdropFilter: "blur(6px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: COLORS.bg2,
              border: `1px solid ${COLORS.gold}`,
              maxWidth: 520,
              width: "100%",
              padding: "36px 36px 32px",
              position: "relative",
              boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
            }}
          >
            <button
              onClick={() => setPendingState(null)}
              data-testid="state-pending-close"
              aria-label="Close"
              style={{
                position: "absolute",
                top: 16,
                right: 16,
                background: "transparent",
                border: "none",
                color: COLORS.muted,
                fontFamily: FONT_STACK_MONO,
                fontSize: 18,
                cursor: "pointer",
                padding: 4,
                lineHeight: 1,
              }}
            >
              ×
            </button>

            <div
              style={{
                fontFamily: FONT_STACK_MONO,
                fontSize: 10,
                letterSpacing: "0.32em",
                color: COLORS.gold,
                textTransform: "uppercase",
                marginBottom: 14,
              }}
            >
              § {pendingState} · In Progress
            </div>

            <h3
              style={{
                fontFamily: FONT_STACK_SERIF,
                fontWeight: 500,
                fontSize: 22,
                lineHeight: 1.2,
                color: COLORS.cream,
                marginBottom: 14,
                letterSpacing: "-0.01em",
              }}
              data-testid="state-pending-title"
            >
              Your state report is currently being generated.
            </h3>

            <p
              style={{
                fontSize: 14,
                lineHeight: 1.6,
                color: "#FFFFFF",
                marginBottom: 22,
              }}
              data-testid="state-pending-body"
            >
              To request priority processing, contact us at{" "}
              <a
                href="mailto:CMS@MendRx.ai?subject=Priority%20State%20Report%20Request"
                style={{ color: COLORS.gold, textDecoration: "none", fontWeight: 600 }}
                data-testid="state-pending-email"
              >
                CMS@MendRx.ai
              </a>
              .
            </p>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <a
                href={`mailto:CMS@MendRx.ai?subject=Priority%20State%20Report%20Request%20-%20${encodeURIComponent(
                  pendingState
                )}&body=Hello%20MENDRx%20team%2C%0A%0AI'd%20like%20to%20request%20priority%20processing%20of%20the%20${encodeURIComponent(
                  pendingState
                )}%20state%20briefing.%0A%0AThank%20you.`}
                data-testid="state-pending-request-btn"
                style={{
                  background: COLORS.gold,
                  color: COLORS.bg,
                  padding: "12px 22px",
                  fontFamily: FONT_STACK_MONO,
                  fontSize: 11,
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  textDecoration: "none",
                  fontWeight: 600,
                  border: `1px solid ${COLORS.gold}`,
                }}
              >
                ↗ Request Priority
              </a>
              <button
                onClick={() => setPendingState(null)}
                data-testid="state-pending-dismiss-btn"
                style={{
                  background: "transparent",
                  color: COLORS.cream,
                  padding: "12px 22px",
                  fontFamily: FONT_STACK_MONO,
                  fontSize: 11,
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  fontWeight: 500,
                  border: `1px solid ${COLORS.accent}`,
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------
   CARD COMPONENT
   -------------------------------------------------------------- */
function DocumentCard({
  kicker,
  title,
  meta,
  description,
  onView,
  downloadHref,
  testIdPrefix,
}) {
  return (
    <div
      data-testid={`${testIdPrefix}-card`}
      onClick={onView}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onView();
      }}
      style={{
        background: COLORS.bg2,
        border: `1px solid rgba(200,191,166,0.22)`,
        padding: "28px 28px 24px",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        transition: "border-color 200ms ease, transform 200ms ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = COLORS.gold;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "rgba(200,191,166,0.22)";
      }}
    >
      <div
        style={{
          fontFamily: FONT_STACK_MONO,
          fontSize: 10,
          letterSpacing: "0.32em",
          color: COLORS.gold,
          textTransform: "uppercase",
          marginBottom: 16,
        }}
      >
        {kicker}
      </div>

      <h2
        style={{
          fontFamily: FONT_STACK_SERIF,
          fontWeight: 500,
          fontSize: 24,
          lineHeight: 1.15,
          color: COLORS.cream,
          marginBottom: 10,
          letterSpacing: "-0.01em",
        }}
      >
        {title}
      </h2>

      <div
        style={{
          fontFamily: FONT_STACK_MONO,
          fontSize: 10,
          letterSpacing: "0.22em",
          color: COLORS.muted,
          textTransform: "uppercase",
          marginBottom: 16,
        }}
      >
        {meta}
      </div>

      <p
        style={{
          fontSize: 14,
          lineHeight: 1.55,
          color: "#FFFFFF",
          marginBottom: 24,
          flex: 1,
        }}
      >
        {description}
      </p>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button
          type="button"
          data-testid={`${testIdPrefix}-view-btn`}
          onClick={(e) => {
            e.stopPropagation();
            onView();
          }}
          style={{
            background: COLORS.gold,
            color: COLORS.bg,
            padding: "12px 20px",
            fontFamily: FONT_STACK_MONO,
            fontSize: 11,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            fontWeight: 600,
            border: "1px solid " + COLORS.gold,
            cursor: "pointer",
          }}
        >
          View in Browser
        </button>

        <a
          href={downloadHref}
          data-testid={`${testIdPrefix}-download-btn`}
          onClick={(e) => e.stopPropagation()}
          style={{
            display: "inline-block",
            background: "transparent",
            color: COLORS.cream,
            padding: "12px 20px",
            fontFamily: FONT_STACK_MONO,
            fontSize: 11,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            textDecoration: "none",
            border: `1px solid ${COLORS.accent}`,
            fontWeight: 500,
          }}
        >
          ↓ Download
        </a>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------
   STATE GATE – simple password gate that reveals a PDF download
   -------------------------------------------------------------- */
const STATE_GATE_PASSWORD = "MENDRx2026";

function StateGate() {
  const { slug } = useParams();
  const [stateName, setStateName] = useState("");
  const [count, setCount] = useState(null);
  const [pageList, setPageList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [pw, setPw] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/state/${slug}/pages`)
      .then((r) => {
        if (!active) return;
        if (!r.data.count) {
          setNotFound(true);
        } else {
          setCount(r.data.count);
          setPageList(r.data.pages || []);
          setStateName(r.data.state_name || slug);
        }
        setLoading(false);
      })
      .catch(() => {
        setNotFound(true);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [slug]);

  const submit = (e) => {
    e.preventDefault();
    if (pw === STATE_GATE_PASSWORD) {
      setUnlocked(true);
      setError("");
    } else {
      setError("Incorrect password. Please try again.");
    }
  };

  const downloadUrl = `${API}/state/${slug}/download`;
  const downloadFilename = stateName
    ? `MENDRx_State_Briefing_${stateName}.pdf`
    : `MENDRx_State_Briefing.pdf`;

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: COLORS.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: FONT_STACK_MONO,
          fontSize: 11,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: COLORS.muted,
        }}
        data-testid={`state-gate-${slug}-loading`}
      >
        <GoogleFonts />
        Loading briefing…
      </div>
    );
  }

  if (notFound) {
    return (
      <div
        data-testid={`state-gate-${slug}-notfound`}
        style={{
          minHeight: "100vh",
          background: COLORS.bg,
          color: COLORS.cream,
          fontFamily: FONT_STACK_SANS,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          padding: 32,
          gap: 16,
        }}
      >
        <GoogleFonts />
        <div
          style={{
            fontFamily: FONT_STACK_MONO,
            fontSize: 10,
            letterSpacing: "0.3em",
            color: COLORS.gold,
            textTransform: "uppercase",
          }}
        >
          Briefing Not Found
        </div>
        <h1
          style={{
            fontFamily: FONT_STACK_SERIF,
            fontWeight: 500,
            fontSize: 32,
            color: COLORS.cream,
          }}
        >
          No briefing is published for "{slug}".
        </h1>
        <Link
          to="/"
          style={{
            fontFamily: FONT_STACK_MONO,
            fontSize: 11,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: COLORS.bg,
            background: COLORS.gold,
            padding: "13px 24px",
            textDecoration: "none",
            fontWeight: 600,
          }}
        >
          ← Back to Briefings
        </Link>
      </div>
    );
  }

  /* After the user unlocks, render a full-page preview with a sticky download
     bar at top and the entire PDF rendered as page images below. Applies to
     all current and future states. */
  if (unlocked) {
    const downloadUrl = `${API}/state/${slug}/download`;
    const downloadFilename = `MENDRx_State_Briefing_${stateName}.pdf`;
    return (
      <div
        data-testid={`state-gate-${slug}-pilot`}
        style={{
          minHeight: "100vh",
          background: COLORS.bg,
          color: COLORS.label,
          fontFamily: FONT_STACK_SANS,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <GoogleFonts />

        {/* Sticky top bar — Download CTA on the right */}
        <div
          data-testid={`state-gate-${slug}-pilot-topbar`}
          style={{
            background: COLORS.bg2,
            borderBottom: `1px solid rgba(200,191,166,0.22)`,
            padding: "16px 28px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 16,
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <Link
              to="/"
              data-testid={`state-gate-${slug}-pilot-back`}
              style={{
                fontFamily: FONT_STACK_MONO,
                fontSize: 10,
                letterSpacing: "0.28em",
                color: COLORS.gold,
                textTransform: "uppercase",
                textDecoration: "none",
              }}
            >
              ← Back to Briefings
            </Link>
            <div
              style={{
                fontFamily: FONT_STACK_SERIF,
                fontSize: 18,
                color: COLORS.cream,
                fontWeight: 500,
                letterSpacing: "-0.005em",
              }}
            >
              State Policy Briefing — State of {stateName} · April 2026
            </div>
            <div
              style={{
                fontFamily: FONT_STACK_MONO,
                fontSize: 10,
                letterSpacing: "0.24em",
                color: COLORS.muted,
                textTransform: "uppercase",
                marginTop: 2,
              }}
            >
              {count} pages · Preview
            </div>
          </div>

          <a
            href={downloadUrl}
            download={downloadFilename}
            data-testid={`state-gate-${slug}-pilot-download`}
            style={{
              background: COLORS.gold,
              color: COLORS.bg,
              padding: "13px 24px",
              fontFamily: FONT_STACK_MONO,
              fontSize: 11,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              fontWeight: 600,
              border: "1px solid " + COLORS.gold,
              textDecoration: "none",
            }}
          >
            ↓ Download PDF
          </a>
        </div>

        {/* Page render area */}
        <div
          data-testid={`state-gate-${slug}-pilot-pages`}
          style={{
            flex: 1,
            padding: "32px 16px 60px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 24,
            background: "#081424",
          }}
        >
          {pageList.map((p, i) => (
            <img
              key={p}
              src={`${API}/state/${slug}/page/${p}`}
              alt={`${stateName} briefing — page ${i + 1}`}
              data-testid={`state-gate-${slug}-pilot-page-${i + 1}`}
              loading={i < 3 ? "eager" : "lazy"}
              style={{
                width: "min(100%, 760px)",
                height: "auto",
                boxShadow: "0 14px 48px rgba(0,0,0,0.55)",
                border: "1px solid rgba(200,191,166,0.14)",
                display: "block",
                imageRendering: "auto",
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={`state-gate-${slug}`}
      style={{
        minHeight: "100vh",
        background: COLORS.bg,
        color: COLORS.label,
        fontFamily: FONT_STACK_SANS,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <GoogleFonts />

      <div
        style={{
          maxWidth: 520,
          width: "100%",
          background: COLORS.bg2,
          border: `1px solid rgba(200,191,166,0.22)`,
          padding: "44px 40px 36px",
          position: "relative",
        }}
      >
        <Link
          to="/"
          data-testid={`state-gate-${slug}-back`}
          style={{
            position: "absolute",
            top: 16,
            left: 24,
            fontFamily: FONT_STACK_MONO,
            fontSize: 10,
            letterSpacing: "0.28em",
            color: COLORS.gold,
            textTransform: "uppercase",
            textDecoration: "none",
          }}
        >
          ← Back
        </Link>

        <div
          style={{
            fontFamily: FONT_STACK_MONO,
            fontSize: 10,
            letterSpacing: "0.32em",
            color: COLORS.gold,
            textTransform: "uppercase",
            marginBottom: 14,
            marginTop: 8,
          }}
        >
          §  Secure Access
        </div>

        <h1
          style={{
            fontFamily: FONT_STACK_SERIF,
            fontWeight: 500,
            fontSize: 26,
            lineHeight: 1.18,
            color: COLORS.cream,
            marginBottom: 8,
            letterSpacing: "-0.01em",
          }}
          data-testid={`state-gate-${slug}-title`}
        >
          State Policy Briefing — State of {stateName}
        </h1>

        <div
          style={{
            fontFamily: FONT_STACK_MONO,
            fontSize: 10,
            letterSpacing: "0.24em",
            color: COLORS.muted,
            textTransform: "uppercase",
            marginBottom: 28,
          }}
        >
          {count} pages · April 2026
        </div>

        {!unlocked && (
          <form onSubmit={submit} data-testid={`state-gate-${slug}-form`}>
            <p
              style={{
                fontSize: 14,
                lineHeight: 1.6,
                color: "#FFFFFF",
                marginBottom: 22,
              }}
            >
              This briefing is reserved for the governor's office and CMS
              integrity team. Enter the access password to download the PDF.
            </p>

            <label
              htmlFor="state-gate-pw"
              style={{
                fontFamily: FONT_STACK_MONO,
                fontSize: 10,
                letterSpacing: "0.28em",
                textTransform: "uppercase",
                color: COLORS.muted,
                display: "block",
                marginBottom: 8,
              }}
            >
              Access Password
            </label>
            <input
              id="state-gate-pw"
              type="password"
              autoComplete="off"
              autoFocus
              value={pw}
              onChange={(e) => {
                setPw(e.target.value);
                if (error) setError("");
              }}
              data-testid={`state-gate-${slug}-pw-input`}
              style={{
                width: "100%",
                background: COLORS.bg,
                border: `1px solid ${
                  error ? "#E63946" : "rgba(200,191,166,0.28)"
                }`,
                color: COLORS.cream,
                padding: "13px 14px",
                fontFamily: FONT_STACK_MONO,
                fontSize: 14,
                letterSpacing: "0.06em",
                outline: "none",
                marginBottom: 10,
              }}
            />

            {error && (
              <div
                data-testid={`state-gate-${slug}-error`}
                style={{
                  fontFamily: FONT_STACK_MONO,
                  fontSize: 10,
                  letterSpacing: "0.18em",
                  color: "#E63946",
                  marginBottom: 14,
                  textTransform: "uppercase",
                }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid={`state-gate-${slug}-submit`}
              style={{
                width: "100%",
                background: COLORS.gold,
                color: COLORS.bg,
                padding: "14px 20px",
                fontFamily: FONT_STACK_MONO,
                fontSize: 11,
                letterSpacing: "0.24em",
                textTransform: "uppercase",
                fontWeight: 600,
                border: `1px solid ${COLORS.gold}`,
                cursor: "pointer",
                marginTop: 8,
              }}
            >
              Unlock Briefing
            </button>

            <div
              style={{
                marginTop: 20,
                paddingTop: 14,
                borderTop: "1px solid rgba(200,191,166,0.14)",
                fontFamily: FONT_STACK_MONO,
                fontSize: 9,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: COLORS.muted,
              }}
            >
              Don't have the password?{" "}
              <a
                href="mailto:CMS@MendRx.ai?subject=State%20Briefing%20Access"
                style={{ color: COLORS.gold, textDecoration: "none" }}
                data-testid={`state-gate-${slug}-request-access`}
              >
                Request access
              </a>
            </div>
          </form>
        )}

        {unlocked && (
          <div data-testid={`state-gate-${slug}-unlocked`}>
            <div
              style={{
                fontFamily: FONT_STACK_MONO,
                fontSize: 10,
                letterSpacing: "0.28em",
                color: COLORS.gold,
                textTransform: "uppercase",
                marginBottom: 14,
              }}
            >
              ✓ Access granted
            </div>

            <p
              style={{
                fontSize: 14,
                lineHeight: 1.6,
                color: "#FFFFFF",
                marginBottom: 24,
              }}
            >
              Click below to download the {stateName} briefing PDF.
            </p>

            <a
              href={downloadUrl}
              download={downloadFilename}
              data-testid={`state-gate-${slug}-download-btn`}
              style={{
                display: "block",
                textAlign: "center",
                background: COLORS.gold,
                color: COLORS.bg,
                padding: "16px 22px",
                fontFamily: FONT_STACK_MONO,
                fontSize: 12,
                letterSpacing: "0.24em",
                textTransform: "uppercase",
                fontWeight: 600,
                border: `1px solid ${COLORS.gold}`,
                textDecoration: "none",
              }}
            >
              ↓ Download PDF
            </a>

            <div
              style={{
                marginTop: 18,
                fontFamily: FONT_STACK_MONO,
                fontSize: 9,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: COLORS.muted,
                textAlign: "center",
              }}
            >
              File: {downloadFilename}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* --------------------------------------------------------------
   PDF VIEWER PAGE (renders pre-rasterized page images)
   -------------------------------------------------------------- */
function Viewer({
  title,
  docKey,
  downloadUrl,
  downloadFilename,
  testIdPrefix,
  pagesOverride,
  pageUrlBuilder,
  loading: loadingOverride,
  downloadButtonLabel = "↓ Download PDF",
}) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (pagesOverride !== undefined) {
      setPages(pagesOverride);
      setLoading(!!loadingOverride);
      return;
    }
    if (!docKey) return;
    let active = true;
    axios
      .get(`${API}/doc/${docKey}/pages`)
      .then((r) => {
        if (!active) return;
        setPages(r.data.pages || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
    return () => {
      active = false;
    };
  }, [docKey, pagesOverride, loadingOverride]);

  const buildPageUrl =
    pageUrlBuilder || ((p) => `${API}/doc/${docKey}/page/${p}`);

  return (
    <div
      data-testid={`${testIdPrefix}-viewer-page`}
      style={{
        minHeight: "100vh",
        background: COLORS.bg,
        color: COLORS.label,
        fontFamily: FONT_STACK_SANS,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <GoogleFonts />

      {/* TOP BAR with Download button */}
      <div
        style={{
          background: COLORS.bg2,
          borderBottom: `1px solid rgba(200,191,166,0.22)`,
          padding: "18px 32px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 16,
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
        data-testid={`${testIdPrefix}-topbar`}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <Link
            to="/"
            data-testid={`${testIdPrefix}-back-link`}
            style={{
              fontFamily: FONT_STACK_MONO,
              fontSize: 10,
              letterSpacing: "0.28em",
              color: COLORS.gold,
              textTransform: "uppercase",
              textDecoration: "none",
            }}
          >
            ← Back to Briefings
          </Link>
          <div
            style={{
              fontFamily: FONT_STACK_SERIF,
              fontSize: 18,
              color: COLORS.cream,
              fontWeight: 500,
              letterSpacing: "-0.005em",
            }}
            data-testid={`${testIdPrefix}-title`}
          >
            {title}
          </div>
        </div>

        <a
          href={downloadUrl}
          download={downloadFilename}
          data-testid={`${testIdPrefix}-download-top-btn`}
          style={{
            background: COLORS.gold,
            color: COLORS.bg,
            padding: "13px 24px",
            fontFamily: FONT_STACK_MONO,
            fontSize: 11,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            fontWeight: 600,
            border: "1px solid " + COLORS.gold,
            textDecoration: "none",
          }}
        >
          {downloadButtonLabel}
        </a>
      </div>

      {/* PAGE RENDER AREA */}
      <div
        style={{
          flex: 1,
          padding: "32px 16px 60px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 24,
          background: "#081424",
        }}
        data-testid={`${testIdPrefix}-pages-container`}
      >
        {loading && (
          <div
            style={{
              color: COLORS.muted,
              fontFamily: FONT_STACK_MONO,
              fontSize: 11,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              marginTop: 40,
            }}
            data-testid={`${testIdPrefix}-loading`}
          >
            Loading briefing…
          </div>
        )}

        {!loading && pages.length === 0 && (
          <div style={{ color: COLORS.muted }}>No pages found.</div>
        )}

        {pages.map((p, i) => (
          <img
            key={p}
            src={buildPageUrl(p)}
            alt={`${title} — page ${i + 1}`}
            data-testid={`${testIdPrefix}-page-${i + 1}`}
            style={{
              width: "min(100%, 1100px)",
              height: "auto",
              boxShadow: "0 14px 48px rgba(0,0,0,0.55)",
              border: "1px solid rgba(200,191,166,0.14)",
              display: "block",
            }}
          />
        ))}
      </div>
    </div>
  );
}

/* --------------------------------------------------------------
   APP ROUTER
   -------------------------------------------------------------- */
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route
            path="/view/onepager"
            element={
              <Viewer
                title="30 Days to Strengthen Medicaid — One-Pager"
                docKey="onepager"
                downloadUrl={`${API}/briefing/download`}
                downloadFilename="30_Days_to_Strengthen_Medicaid.pdf"
                testIdPrefix="onepager"
              />
            }
          />
          <Route
            path="/view/deck"
            element={
              <Viewer
                title="MENDRx Governors' Briefing — Full Deck"
                docKey="deck"
                downloadUrl={`${API}/deck/download`}
                downloadFilename="MENDRx_Governors_Briefing_Full_Deck.pdf"
                testIdPrefix="deck"
              />
            }
          />
          <Route path="/view/state/:slug" element={<StateGate />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
